<template>
  <LogworkStickyWrapper ref="sticky">
    <table class="table table-bordered logwork-table">
      <LogworkTableHeader
        :monthHeaders="monthHeaders"
        :weekHeaders="weekHeaders"
        :dayColumns="dayColumns"
        :viewMode="viewMode"
      />
      <LogworkTableBody
        :allRows="allRows"
        :dayColumns="dayColumns"
        :itemType="itemType"
        :expandableRows="expandableRows"
        :expandedRows="internalExpandedRows"
        :loadingRows="loadingRows"
        :viewMode="viewMode"
        :isShowHour="isShowHour"
        :expandTrigger="expandTrigger"
        @toggle-expand="toggleExpand"
        @load-more-child="loadMoreChild"
        @view-detail="$emit('view-detail', $event)"
        @create-logwork="$emit('create-logwork', $event)"
      />
    </table>
  </LogworkStickyWrapper>
</template>

<script>
import LogworkService from "@/api/logwork";

export default {
  name: "LogworkTable",
  data() {
    return {
      // Internal expanded rows (separate from prop to avoid prop/data collision)
      internalExpandedRows: [],
      childRows: {}, // { parentId: [childRows] }
      loadingRows: [], // track loading state (array for Vue reactivity)
      childPaging: {} // { parentId: { page, hasMore, loadingMore } }
    };
  },
  props: {
    loading: { type: Boolean, default: false },
    issueHeader: { type: String, default: "" },
    sumHeader: { type: String, default: "" },
    dayColumns: { type: Array, required: true },
    monthHeaders: { type: Array, required: true },
    weekHeaders: { type: Array, required: true },
    displayedRows: { type: Array, required: true },
    viewMode: {
      type: String,
      default: "week",
      validator: v => ["day", "week", "month"].includes(v)
    },
    // API parameters
    view: { type: String, default: "week" },
    fromDate: { type: String, required: true },
    toDate: { type: String, required: true },
    groupBy: { type: String, default: "project" },
    memberIds: { type: [Array, String], default: () => [] },
    projectIds: { type: [Array, String], default: () => [] },
    itemType: { type: String, default: "summary" },
    // Expansion props
    expandableRows: { type: Boolean, default: false },
    // Parent-controlled expanded rows (optional) — if provided, we sync to internal state
    expandedRows: { type: Array, default: () => [] },
    isShowHour: { type: Boolean, default: true },
    expandTrigger: Number
  },
  watch: {
    dayColumns: {
      handler() {
        this.$nextTick(
          () => this.$refs.sticky && this.$refs.sticky.updateOffsets()
        );
      },
      deep: true
    },
    monthHeaders: {
      handler() {
        this.$nextTick(
          () => this.$refs.sticky && this.$refs.sticky.updateOffsets()
        );
      },
      deep: true
    },
    weekHeaders: {
      handler() {
        this.$nextTick(
          () => this.$refs.sticky && this.$refs.sticky.updateOffsets()
        );
      },
      deep: true
    },
    displayedRows() {
      // Row height changes can affect scrollbar height
      this.$nextTick(
        () => this.$refs.sticky && this.$refs.sticky.updateOffsets()
      );
    },

    // Sync parent-provided expandedRows prop into our internal state
    expandedRows: {
      handler(newVal) {
        this.internalExpandedRows = Array.isArray(newVal) ? [...newVal] : [];
      },
      immediate: true
    }
  },
  components: {
    LogworkTableHeader: () => import("./LogworkTableHeader.vue"),
    LogworkTableBody: () => import("./LogworkTableBody.vue"),
    LogworkStickyWrapper: () => import("./LogworkStickyWrapper.vue")
  },
  computed: {
    allRows() {
      const result = [];

      this.displayedRows.forEach(row => {
        result.push(row);
        if (
          this.internalExpandedRows.includes(row.id) &&
          this.childRows[row.id]
        ) {
          // Add child rows with increased level
          this.childRows[row.id].forEach(childRow => {
            result.push({
              ...childRow,
              level: (childRow.level || 0) + (row.level || 0) + 1,
              isChild: true,
              parentId: row.id
            });
          });

          const meta = this.childPaging[row.id];
          if (meta && meta.hasMore) {
            result.push({
              id: `loadmore-${row.id}`,
              isChild: true,
              isLoadMore: true,
              parentId: row.id,
              level: (row.level || 0) + 1
            });
          }
        }
      });
      return result;
    }
  },
  methods: {
    // Clear internal state (used when filters change) to avoid showing stale child rows
    clearState() {
      this.internalExpandedRows = [];
      this.childRows = {};
      this.loadingRows = [];
      this.childPaging = {};
    },

    formatHour(value) {
      if (!value) return 0;
      return Number(value.toFixed(1))
        .toString()
        .replace(/\.0$/, "");
    },
    barStyle(hours) {
      const width = Math.min(100, (hours / 10) * 100);
      return { width: `${width}%` };
    },
    rowRowClass(row) {
      return {
        "group-row": row.isGroup,
        "level-1": row.level === 1,
        "child-row": row.isChild
      };
    },
    async toggleExpand(row) {
      const rowId = row.id;
      if (this.internalExpandedRows.includes(rowId)) {
        // Collapse
        this.internalExpandedRows = this.internalExpandedRows.filter(
          id => id !== rowId
        );
        this.$emit("toggle-expansion", { rowId, expanded: false });
      } else {
        // Expand
        if (!this.childRows[rowId]) {
          await this.fetchChildItems(rowId);
        }
        this.internalExpandedRows = [...this.internalExpandedRows, rowId];
        this.$emit("toggle-expansion", { rowId, expanded: true });
      }
    },

    async fetchChildItems(parentId, append = false) {
      const meta = this.childPaging[parentId] || {
        page: 0,
        hasMore: true,
        loadingMore: false
      };

      if (!meta.hasMore && append) return;
      if (meta.loadingMore) return;

      if (!append) this.loadingRows.push(parentId);
      this.childPaging = {
        ...this.childPaging,
        [parentId]: { ...meta, loadingMore: append }
      };

      const nextPage = append ? meta.page + 1 : 1;

      try {
        const params = {
          view: this.view,
          from_date: this.fromDate,
          to_date: this.toDate,
          group_by: this.groupBy,
          item_type: this.itemType,
          per_page: 50,
          page: nextPage
        };

        if (this.memberIds && this.memberIds.length > 0) {
          params.member_ids = Array.isArray(this.memberIds)
            ? this.memberIds.join(",")
            : this.memberIds;
        }
        if (this.projectIds && this.projectIds.length > 0) {
          params.project_ids = Array.isArray(this.projectIds)
            ? this.projectIds.join(",")
            : this.projectIds;
        }

        const response = await LogworkService.getTimelineItems(
          parentId,
          params
        );

        const rawData = response.data?.data || [];
        const transformedData = this.transformChildItems(rawData, parentId);

        const metaResp = response.data?.meta;
        const hasMore = metaResp
          ? metaResp.current_page < metaResp.last_page
          : rawData.length === params.per_page;

        const existing = this.childRows[parentId] || [];
        const merged = append ? existing.concat(transformedData) : transformedData;

        this.$set(this.childRows, parentId, merged);
        this.childPaging = {
          ...this.childPaging,
          [parentId]: {
            page: nextPage,
            hasMore,
            loadingMore: false
          }
        };
      } catch (error) {
        console.error("Error fetching child items:", error);
        this.$set(this.childRows, parentId, []);
        this.childPaging = {
          ...this.childPaging,
          [parentId]: { page: 0, hasMore: false, loadingMore: false }
        };
      } finally {
        this.loadingRows = this.loadingRows.filter(id => id !== parentId);
      }
    },

    async loadMoreChild(parentId) {
      await this.fetchChildItems(parentId, true);
    },
    transformChildItems(items, parentId) {
      return (items || []).map(item => ({
        id: item.id || item.employee_id,
        employee_id: item.employee_id,
        project_id: item.project_id,
        project_manager_id: item.project_manager_id,
        label:
          item.employee_name ||
          item.name ||
          item.issue_summary ||
          item.issue_title ||
          item.title ||
          "",
        total: item.total || 0,
        values: item.summary || {},
        level: 1, // Child level
        isGroup: false,
        isChild: true,
        parentId: parentId
      }));
    },

    updateStickyOffsets() {
      // Simplify: pin first column at left 0, second at width of first.
      // This avoids gaps caused by scroll math and layout rounding.
      const wrapper = this.$el.querySelector(".logwork-table-wrapper");
      if (!wrapper) return;
      const table = wrapper.querySelector("table");
      if (!table) return;

      const issueTh = table.querySelector("th.col-issue");
      const sumTh = table.querySelector("th.col-sum");
      const issueWidth = issueTh ? Math.round(issueTh.offsetWidth) : 220;
      const sumWidth = sumTh ? Math.round(sumTh.offsetWidth) : 70;
      const frozenWidth = issueWidth + sumWidth;

      wrapper.style.setProperty("--col0-left", `0px`);
      wrapper.style.setProperty("--col1-left", `${issueWidth}px`);
      wrapper.style.setProperty("--frozen-width", `${frozenWidth}px`);
      // Subtract wrapper borders (~2px) to better fit the native scrollbar height
      const sbH = Math.max(0, wrapper.offsetHeight - wrapper.clientHeight - 2);
      wrapper.style.setProperty("--sb-h", `${sbH}px`);
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.sticky && this.$refs.sticky.updateOffsets) {
        this.$refs.sticky.updateOffsets();
      }
    });
  },
  beforeDestroy() {
    // Sticky wrapper handles its own cleanup
  }
};
</script>

<style scoped>
:root {
  --col0-left: 0px;
  --col1-left: 220px;
  --frozen-width: 290px; /* default: issue(220) + sum(70) */
  --sb-h: 16px;
}
.logwork-table-wrapper {
  position: relative;
}
.card {
  border-radius: 8px;
  overflow: hidden;
}
.card-body {
  padding: 0px;
}
/* Use separate border model so sticky column borders can be drawn independently to avoid shared transparent gaps */
.logwork-table {
  border-collapse: collapse;
  border-spacing: 0;
}
.logwork-table .col-issue {
  min-width: 220px;
  text-align: left;
}
.logwork-table .col-sum {
  width: 70px;
}
.logwork-table tbody td {
  vertical-align: middle;
  text-align: center;
  padding: 6px;
  background: #fff;
}

/* Sticky/frozen first column (project) – ::v-deep so styles apply to child components' th/td */
::v-deep .logwork-table th.col-issue {
  position: sticky;
  left: var(--col0-left);
  z-index: 15; /* above normal cells */
  background: #fff;
  background-clip: padding-box;
}
::v-deep .logwork-table td.issue-cell {
  position: sticky;
  left: var(--col0-left);
  z-index: 15;
  background: #fff;
  background-clip: padding-box;
}

/* Sticky/frozen second column (sum) – keep right border visible when scrolling */
::v-deep .logwork-table th.col-sum {
  position: sticky;
  left: var(--col1-left);
  z-index: 14;
  background: #fff;
  background-clip: padding-box;
  /* White line on right so border stays visible on blue header when scrolling */
  box-shadow: 2px 0 0 0 rgba(255, 255, 255, 0.9);
}
::v-deep .logwork-table td.sum-cell {
  position: sticky;
  left: var(--col1-left);
  z-index: 14;
  background: #fff;
  background-clip: padding-box;
  box-shadow: 2px 0 0 -1px #D3EDFF;
}

::v-deep .logwork-table.table-bordered thead th:not(:last-child) {
  border-right: 1px solid #fff;
  border-bottom: 1px solid #fff;
}

::v-deep .logwork-table thead th:not(.col-issue):not(.col-sum) {
  background-color: #007aff;
  color: #fff;
  text-align: center;
}

/* Raise header cells above other content */
::v-deep .logwork-table thead th.col-issue,
::v-deep .logwork-table thead th.col-sum {
  background-color: #007aff;
  color: #fff;
  z-index: 17;
  vertical-align: middle;
}

.table-bordered thead th.col-week {
  background-color: #b5d1f3;
  color: #13161c;
  padding: 5px;
  border: 0px;
}
.table-bordered thead th.col-day {
  padding: 5px;
}

.issue-cell {
  text-align: left;
  font-weight: 500;
}

.issue-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.expand-btn {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 3px;
  transition: all 0.2s;
  font-size: 12px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.expand-btn:hover:not(:disabled) {
  background-color: #f0f0f0;
  color: #333;
}

.expand-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.expand-btn i {
  font-size: 10px;
}
.sum-cell {
  font-weight: 700;
  background: #f9f9f9;
}

.child-row {
  background-color: #fafafa;
}

.load-more-row td {
  text-align: center;
  background-color: #fdfdfd;
}

.child-row .issue-cell {
  font-weight: 400;
  color: #666;
}

.log-value {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-height: 40px;
}
.bar {
  width: 100%;
  max-width: 40px;
  height: 3px;
  background: linear-gradient(90deg, #00c9a7, #ffa94d, #ff6b6b);
  border-radius: 2px;
}
.hours {
  font-weight: 600;
  font-size: 12px;
}

::v-deep .logwork-table {
  border-color: #D3EDFF !important;
}

::v-deep .logwork-table th,
::v-deep .logwork-table td {
  border-color: #D3EDFF !important;
}
::v-deep .logwork-table tr.child-row > td {
  background-color: #fff;
}
</style>
